import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { AlertCircle, Mic, Paperclip, Plus, Send, X } from 'lucide-react';
import { useChatStore } from '../../stores/chatStore';
import { useAuthStore } from '../../stores/authStore';
import { cn, formatFileSize, formatShortcut, MAX_FILE_SIZE, MAX_MESSAGE_LENGTH, SUPPORTED_FILE_TYPES, ui } from '../../utils';
import type { FileUpload } from '../../types';
import { AttachmentMenu } from './AttachmentMenu';
import { EmojiPicker } from './EmojiPicker';
import MentionInput, { type MentionParticipant } from './MentionInput';
import OneDrivePicker from './OneDrivePicker';
import PromptTemplatesButton from './PromptTemplatesButton';
import { VoiceButton } from './VoiceButton';
import { VoiceModeButton } from './VoiceModeButton';
import { VoicePicker } from './VoicePicker';

const SLASH_COMMANDS = [
  { value: '/new', label: '/new', description: 'Start a brand-new conversation' },
  { value: '/reset', label: '/reset', description: "Clear the agent's memory in this conversation" },
  { value: '/stop', label: '/stop', description: 'Stop the agent mid-response' },
  { value: '/wait', label: '/wait', description: 'Pause the current reply' },
  { value: '/help', label: '/help', description: 'Show the quick-reference guide' },
] as const;

type BrowserSpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

interface BrowserSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

interface SpeechRecognitionResultLike {
  transcript: string;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<ArrayLike<SpeechRecognitionResultLike>>;
}

function getSpeechRecognitionConstructor(): BrowserSpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const speechWindow = window as typeof window & {
    SpeechRecognition?: BrowserSpeechRecognitionConstructor;
    webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
  };

  return speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition || null;
}

interface MessageInputProps {
  conversationId: string;
  onSend?: (content: string, files?: File[]) => void | Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function MessageInput({
  conversationId,
  onSend,
  placeholder = 'Message Ally...',
  disabled = false,
  className,
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<FileUpload[]>([]);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isOneDriveOpen, setIsOneDriveOpen] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  const [isMobileOverflowOpen, setIsMobileOverflowOpen] = useState(false);
  const [isSpeechListening, setIsSpeechListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const speechRecognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mobileOverflowRef = useRef<HTMLDivElement>(null);
  const lastSuggestionRequestIdRef = useRef<number | null>(null);

  const connectionState = useAuthStore((state) => state.connectionState);
  const sendMessage = useChatStore((state) => state.sendMessage);
  const composerDraft = useChatStore((state) => state.composerDrafts?.[conversationId] || '');
  const suggestionDispatchVersion = useChatStore((state) => state.suggestionDispatchVersion?.[conversationId] || 0);
  const clearComposerDraft = useChatStore((state) => state.clearComposerDraft ?? (() => undefined));
  const voiceEnabled = useChatStore((state) => state.voiceEnabled);
  const conversationMessages = useChatStore((state) => state.messages?.[conversationId] || []);
  const conversations = useChatStore((state) => state.conversations ?? []);
  const agents = useChatStore((state) => state.agents ?? []);

  const conversation = useMemo(
    () => conversations.find((item) => item.id === conversationId) ?? null,
    [conversationId, conversations],
  );

  const mentionParticipants = useMemo<MentionParticipant[]>(() => {
    if (!conversation?.isGroup) {
      return [];
    }

    const groupAgentIds = new Set(
      (conversation.participants ?? [])
        .filter((participant) => participant.participantType === 'AGENT')
        .map((participant) => participant.participantId),
    );

    return agents
      .filter((agent) => groupAgentIds.has(agent.id))
      .sort((left, right) => left.name.localeCompare(right.name))
      .map((agent) => ({
        id: agent.id,
        name: agent.name,
        type: 'agent',
        avatar: agent.avatar,
      }));
  }, [agents, conversation]);

  const latestAssistantMessage = useMemo(
    () => [...conversationMessages].reverse().find((entry) => entry.role === 'assistant')?.content,
    [conversationMessages],
  );

  const slashMatches = useMemo(() => {
    const trimmed = message.trimStart();
    if (!trimmed.startsWith('/')) return [];
    if (trimmed.includes(' ')) return [];
    const query = trimmed.toLowerCase();
    return SLASH_COMMANDS.filter((command) => command.value.toLowerCase().startsWith(query));
  }, [message]);

  const showSlashMenu = slashMatches.length > 0;
  const hasCompletedFiles = attachedFiles.some((file) => file.status === 'completed');
  const sendShortcut = formatShortcut(['Mod', 'Enter']);
  const hasContent = message.trim().length > 0 || hasCompletedFiles;
  const isOverLimit = message.length > MAX_MESSAGE_LENGTH;
  const hasUploadErrors = attachedFiles.some((file) => file.status === 'error') || uploadErrors.length > 0;
  const isInputDisabled = disabled || isSubmitting || connectionState !== 'connected';
  const showSpeechButton = speechSupported;

  useEffect(() => {
    setSpeechSupported(Boolean(getSpeechRecognitionConstructor()));
  }, []);

  useEffect(() => {
    if (slashIndex >= slashMatches.length) {
      setSlashIndex(0);
    }
  }, [slashIndex, slashMatches.length]);

  useEffect(() => {
    if (!isMobileOverflowOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (mobileOverflowRef.current && !mobileOverflowRef.current.contains(event.target as Node)) {
        setIsMobileOverflowOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isMobileOverflowOpen]);

  useEffect(() => () => {
    speechRecognitionRef.current?.stop();
  }, []);

  const addFiles = useCallback((files: File[]) => {
    const nextErrors: string[] = [];
    const nextUploads: FileUpload[] = [];

    files.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        nextErrors.push(`${file.name} is too large (max ${formatFileSize(MAX_FILE_SIZE)})`);
        return;
      }

      const extension = file.name.split('.').pop()?.toLowerCase();
      // Browsers often report .zip files as application/octet-stream (or
      // application/x-zip-compressed on Windows), so fall back to the file
      // extension for known-safe types. Disallowed types (.exe, etc.) have no
      // mapping here and are still rejected below.
      const extToMime: Record<string, string> = {
        md: 'text/markdown',
        markdown: 'text/markdown',
        json: 'application/json',
        csv: 'text/csv',
        txt: 'text/plain',
        zip: 'application/zip',
      };
      const effectiveType = SUPPORTED_FILE_TYPES.includes(file.type as any)
        ? file.type
        : extension && extToMime[extension]
          ? extToMime[extension]
          : file.type;

      if (effectiveType && !SUPPORTED_FILE_TYPES.includes(effectiveType as any)) {
        nextErrors.push(`${file.name} is not a supported file type`);
        return;
      }

      nextUploads.push({ file, progress: 100, status: 'completed' });
    });

    if (nextErrors.length > 0) {
      setUploadErrors((current) => [...current, ...nextErrors]);
    }
    if (nextUploads.length > 0) {
      setAttachedFiles((current) => [...current, ...nextUploads]);
    }
  }, []);

  const handleScreenshot = useCallback(async () => {
    let stream: MediaStream | null = null;

    try {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new Error('Screen capture is not supported in this browser');
      }

      stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const track = stream.getVideoTracks()[0];
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;

      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error('Unable to load screen capture stream'));
      });

      await video.play();

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')?.drawImage(video, 0, 0);
      track.stop();

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((nextBlob) => resolve(nextBlob), 'image/png');
      });

      if (!blob) {
        throw new Error('Unable to create screenshot file');
      }

      addFiles([new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' })]);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        console.log('Screenshot cancelled or not supported');
        return;
      }

      console.error('Screenshot failed', error);
      setUploadErrors((current) => [...current, error instanceof Error ? error.message : 'Screenshot failed']);
    } finally {
      stream?.getTracks().forEach((track) => track.stop());
    }
  }, [addFiles]);

  const handleScreenRecord = useCallback(async () => {
    let stream: MediaStream | null = null;

    try {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new Error('Screen recording is not supported in this browser');
      }

      if (typeof MediaRecorder === 'undefined') {
        throw new Error('MediaRecorder is not supported in this browser');
      }

      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });

      const recorder = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9' })
        : new MediaRecorder(stream);
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        addFiles([new File([blob], `recording-${Date.now()}.webm`, { type: 'video/webm' })]);
        stream?.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
        recorderRef.current = null;
      };

      recorder.start();
      recorderRef.current = recorder;
      setIsRecording(true);

      stream.getVideoTracks()[0].onended = () => {
        if (recorder.state === 'recording') recorder.stop();
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        console.log('Screen record cancelled or not supported');
      } else {
        console.error('Screen recording failed', error);
        setUploadErrors((current) => [...current, error instanceof Error ? error.message : 'Screen recording failed']);
      }

      stream?.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
    }
  }, [addFiles]);

  const handleStopRecording = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const handlePhotos = useCallback(() => {
    photoInputRef.current?.click();
  }, []);

  const handleFiles = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleClipboard = useCallback(async () => {
    try {
      const items = await navigator.clipboard.read();
      const files: File[] = [];
      for (const item of items) {
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            const blob = await item.getType(type);
            const extension = type.split('/')[1] || 'png';
            files.push(new File([blob], `clipboard-${Date.now()}.${extension}`, { type }));
          }
        }
      }
      if (files.length > 0) {
        addFiles(files);
      }
    } catch (error) {
      console.log('Clipboard read failed', error);
      setUploadErrors((current) => [...current, 'Clipboard access was blocked by the browser']);
    }
  }, [addFiles]);

  const handleOneDrive = useCallback(() => {
    setIsOneDriveOpen(true);
  }, []);

  const appendTranscript = useCallback((text: string) => {
    const normalized = text.trim();
    if (!normalized) {
      return;
    }

    setMessage((current) => (current.trim().length > 0 ? `${current.trimEnd()} ${normalized}` : normalized));
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  const stopSpeechRecognition = useCallback(() => {
    speechRecognitionRef.current?.stop();
    speechRecognitionRef.current = null;
    setIsSpeechListening(false);
  }, []);

  const startSpeechRecognition = useCallback(() => {
    if (isInputDisabled || !speechSupported) {
      return;
    }

    if (speechRecognitionRef.current) {
      stopSpeechRecognition();
      return;
    }

    const Recognition = getSpeechRecognitionConstructor();
    if (!Recognition) {
      setSpeechSupported(false);
      return;
    }

    const recognition = new Recognition();
    let finalTranscript = '';

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';
    recognition.onresult = (event) => {
      finalTranscript = Array.from(event.results)
        .map((result) => result?.[0]?.transcript || '')
        .join(' ')
        .trim();
    };
    recognition.onerror = () => {
      setIsSpeechListening(false);
      speechRecognitionRef.current = null;
    };
    recognition.onend = () => {
      speechRecognitionRef.current = null;
      setIsSpeechListening(false);
      if (finalTranscript) {
        appendTranscript(finalTranscript);
      }
    };

    try {
      speechRecognitionRef.current = recognition;
      recognition.start();
      setIsSpeechListening(true);
    } catch (error) {
      console.error('Speech recognition failed to start', error);
      speechRecognitionRef.current = null;
      setIsSpeechListening(false);
    }
  }, [appendTranscript, isInputDisabled, speechSupported, stopSpeechRecognition]);

  const handleSend = useCallback(async (overrideContent?: string) => {
    const contentToSend = (overrideContent ?? message).trim();
    const filesToSend = attachedFiles.filter((file) => file.status === 'completed').map((file) => file.file);

    if ((!contentToSend && filesToSend.length === 0) || isInputDisabled || isOverLimit || hasUploadErrors) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (onSend) {
        if (filesToSend.length > 0) {
          await Promise.resolve(onSend(contentToSend, filesToSend));
        } else {
          await Promise.resolve(onSend(contentToSend));
        }
      } else if (filesToSend.length > 0) {
        await sendMessage(conversationId, contentToSend, filesToSend);
      } else {
        await sendMessage(conversationId, contentToSend);
      }

      setMessage('');
      setAttachedFiles([]);
      setUploadErrors([]);
      requestAnimationFrame(() => textareaRef.current?.focus());
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [attachedFiles, conversationId, hasUploadErrors, isInputDisabled, isOverLimit, message, onSend, sendMessage]);

  useEffect(() => {
    if (!composerDraft || suggestionDispatchVersion <= 0) {
      return;
    }

    if (lastSuggestionRequestIdRef.current === suggestionDispatchVersion) {
      return;
    }
    lastSuggestionRequestIdRef.current = suggestionDispatchVersion;

    const suggestionText = composerDraft.trim();
    if (!suggestionText) {
      clearComposerDraft(conversationId);
      return;
    }

    setMessage(suggestionText);
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      void handleSend(suggestionText).finally(() => {
        clearComposerDraft(conversationId);
      });
    });
  }, [clearComposerDraft, composerDraft, conversationId, handleSend, suggestionDispatchVersion]);

  const pickSlashCommand = useCallback((command: (typeof SLASH_COMMANDS)[number]) => {
    setMessage(`${command.value} `);
    setSlashIndex(0);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (showSlashMenu) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSlashIndex((index) => (index + 1) % slashMatches.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSlashIndex((index) => (index - 1 + slashMatches.length) % slashMatches.length);
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        const picked = slashMatches[slashIndex] || slashMatches[0];
        if (picked) pickSlashCommand(picked);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setMessage('');
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) addFiles(files);
    event.target.value = '';
  };

  const extractTransferFiles = (items?: DataTransferItemList | null, files?: FileList | null) => {
    const nextFiles: File[] = [];

    if (items) {
      for (let index = 0; index < items.length; index += 1) {
        if (items[index].kind !== 'file') continue;
        const file = items[index].getAsFile();
        if (file) nextFiles.push(file);
      }
    }

    if (nextFiles.length === 0 && files) {
      return Array.from(files);
    }

    return nextFiles;
  };

  const handlePaste = useCallback((event: React.ClipboardEvent) => {
    const files = extractTransferFiles(event.clipboardData?.items, event.clipboardData?.files);
    if (files.length > 0) {
      event.preventDefault();
      addFiles(files);
    }
  }, [addFiles]);

  const removeFile = (index: number) => {
    setAttachedFiles((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const dismissError = (index: number) => {
    setUploadErrors((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleEmojiSelect = (emoji: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const nextMessage = message.slice(0, start) + emoji + message.slice(end);
    setMessage(nextMessage);

    setTimeout(() => {
      textarea.focus();
      const caretPosition = start + emoji.length;
      textarea.setSelectionRange(caretPosition, caretPosition);
    }, 0);
  };

  const handleTemplateInsert = React.useCallback((content: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setMessage((current) => (current ? `${current}
${content}` : content));
      return;
    }

    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;
    const nextMessage = message.slice(0, start) + content + message.slice(end);
    setMessage(nextMessage);

    requestAnimationFrame(() => {
      textarea.focus();
      const caretPosition = start + content.length;
      textarea.setSelectionRange(caretPosition, caretPosition);
    });
  }, [message]);

  const handleDragOver = (event: React.DragEvent) => {
    if (extractTransferFiles(event.dataTransfer?.items, event.dataTransfer?.files).length === 0) {
      return;
    }

    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    const files = extractTransferFiles(event.dataTransfer?.items, event.dataTransfer?.files);
    if (files.length === 0) {
      setIsDragOver(false);
      return;
    }

    event.preventDefault();
    setIsDragOver(false);
    addFiles(files);
  };

  const connectionStatusText = {
    disconnected: 'Reconnect to message Ally.',
    connecting: 'Connecting to Ally...',
    connected: '',
    reconnecting: 'Reconnecting to Ally...',
  }[connectionState];

  return (
    <div className={cn('border-t border-border bg-card/95 backdrop-blur', className)}>
      {connectionStatusText ? (
        <div className="border-b border-amber-500/20 bg-amber-500/10 px-4 py-2 text-sm text-amber-200">
          {connectionStatusText}
        </div>
      ) : null}

      {uploadErrors.length > 0 ? (
        <div className="space-y-2 px-4 pt-3">
          {uploadErrors.map((error, index) => (
            <div key={`${error}-${index}`} className="flex items-center justify-between rounded-xl border border-red-500/20 bg-red-500/10 p-3 shadow-sm shadow-red-950/10">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-300" />
                <span className="text-sm text-red-200">{error}</span>
              </div>
              <button type="button" onClick={() => dismissError(index)} className="rounded-lg p-1 text-red-300 transition-colors duration-150 hover:bg-red-500/10 hover:text-white" aria-label="Dismiss upload error">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {attachedFiles.length > 0 ? (
        <div className="px-4 pt-3">
          <div className="flex flex-wrap gap-2">
            {attachedFiles.map((fileUpload, index) => (
              <FilePreview
                key={`${fileUpload.file.name}-${index}`}
                fileUpload={fileUpload}
                onRemove={() => removeFile(index)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {showSlashMenu ? (
        <div className="mx-4 mt-3 overflow-hidden rounded-[1.25rem] border border-border bg-card shadow-xl shadow-black/10">
          <div className="bg-background/80 px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">Commands</div>
          {slashMatches.map((command, index) => (
            <button
              key={command.value}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                pickSlashCommand(command);
              }}
              onMouseEnter={() => setSlashIndex(index)}
              className={cn(
                'flex min-h-[48px] w-full items-baseline gap-3 px-3 py-2 text-left transition-colors duration-150',
                index === slashIndex ? 'bg-background' : 'hover:bg-background/80',
              )}
              aria-label={`Insert command ${command.label}`}
            >
              <span className="font-mono text-sm text-foreground">{command.label}</span>
              <span className="text-xs text-muted-foreground">{command.description}</span>
            </button>
          ))}
          <div className="bg-background/80 px-3 py-1.5 text-[10px] text-muted-foreground">↑↓ to navigate · Tab to complete · Esc to dismiss</div>
        </div>
      ) : null}

      <div className="p-4">
        <div
          className={cn(
            // D4-11 (FE-4.14): the textarea suppresses its own outline, so the
            // composer container must show focus — keeps one focus language.
            'chat-input-container relative flex min-h-[56px] items-end rounded-[1.25rem] border border-border bg-background transition-colors duration-150 shadow-sm shadow-black/5 focus-within:border-blue-500/60',
            isDragOver && 'border-blue-500 bg-blue-500/5',
            isInputDisabled && 'cursor-not-allowed opacity-60',
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isDragOver ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-blue-500 bg-blue-500/10">
              <div className="text-center">
                <Paperclip className="mx-auto mb-2 h-8 w-8 text-blue-300" />
                <p className="text-sm font-medium text-blue-200">Drop files to attach</p>
              </div>
            </div>
          ) : null}

          <div className="flex items-end p-2">
            <div className="hidden md:block">
              <AttachmentMenu
                disabled={isInputDisabled}
                isRecording={isRecording}
                onScreenshot={() => void handleScreenshot()}
                onScreenRecord={() => void handleScreenRecord()}
                onStopRecording={handleStopRecording}
                onPhotos={handlePhotos}
                onFiles={handleFiles}
                onOneDrive={handleOneDrive}
                onClipboard={() => void handleClipboard()}
              />
            </div>

            <div className="relative md:hidden" ref={mobileOverflowRef}>
              <button
                type="button"
                onClick={() => setIsMobileOverflowOpen((open) => !open)}
                disabled={isInputDisabled}
                className={cn(
                  ui.button.icon,
                  isMobileOverflowOpen && 'bg-background text-foreground',
                )}
                aria-label="Open composer actions"
                aria-haspopup="menu"
                aria-expanded={isMobileOverflowOpen}
              >
                <Plus className="h-5 w-5" />
              </button>

              {isMobileOverflowOpen ? (
                <div className="absolute bottom-full left-0 z-50 mb-2 w-60 overflow-hidden rounded-[1.25rem] border border-border bg-card py-2 shadow-2xl shadow-black/30" role="menu" aria-label="Composer actions">
                  <PromptTemplatesButton
                    disabled={isInputDisabled}
                    onInsert={(content) => {
                      handleTemplateInsert(content);
                      setIsMobileOverflowOpen(false);
                    }}
                    buttonClassName="w-full justify-start rounded-none border-0 bg-transparent px-3 py-2 text-foreground hover:border-transparent hover:bg-background hover:text-foreground-bright"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileOverflowOpen(false);
                      handleFiles();
                    }}
                    className="flex min-h-[44px] w-full items-center gap-3 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Attach file"
                  >
                    <Paperclip className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Attach file</span>
                  </button>
                  <VoiceButton
                    onTranscription={appendTranscript}
                    agentResponse={latestAssistantMessage}
                    voiceEnabled={voiceEnabled}
                    disabled={isInputDisabled}
                    showLabel
                    className="rounded-none px-3 py-2 text-foreground hover:bg-background hover:text-foreground-bright"
                  />
                </div>
              ) : null}
            </div>
          </div>

          {conversation?.isGroup ? (
            <MentionInput
              ref={textareaRef}
              value={message}
              onChangeValue={setMessage}
              participants={mentionParticipants}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onFocus={() => {
                window.scrollTo(0, 0);
                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;
              }}
              placeholder={placeholder}
              disabled={isInputDisabled}
              maxRows={5}
            />
          ) : (
            <TextareaAutosize
              ref={textareaRef}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onFocus={() => {
                window.scrollTo(0, 0);
                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;
              }}
              placeholder={placeholder}
              disabled={isInputDisabled}
              maxRows={5}
              className={cn(
                'flex-1 resize-none border-none bg-transparent px-2 py-3 text-base text-foreground outline-none placeholder:text-muted-foreground',
                'focus:border-none focus:outline-none focus:ring-0 disabled:cursor-not-allowed',
              )}
              aria-label="Message composer"
            />
          )}

          <div className="relative flex items-center gap-1 p-2">
            {message.length > MAX_MESSAGE_LENGTH * 0.8 ? (
              <span className={cn('mr-2 text-xs font-mono', isOverLimit ? 'text-red-300' : 'text-amber-300')}>
                {message.length}/{MAX_MESSAGE_LENGTH}
              </span>
            ) : null}

            <div className="hidden md:flex md:items-center md:gap-1">
              <PromptTemplatesButton disabled={isInputDisabled} onInsert={handleTemplateInsert} />
              <VoicePicker />
              <VoiceButton
                onTranscription={appendTranscript}
                agentResponse={latestAssistantMessage}
                voiceEnabled={voiceEnabled}
                disabled={isInputDisabled}
              />
            </div>
            {/* Voice Mode (pilot): renders nothing unless the availability
                probe says the user is flagged in — zero non-pilot footprint. */}
            <VoiceModeButton
              conversationId={conversationId}
              agentId={conversation?.agentId ?? null}
              disabled={isInputDisabled}
            />
            <EmojiPicker disabled={isInputDisabled} onSelect={handleEmojiSelect} />

            {showSpeechButton ? (
              <button
                type="button"
                onClick={isSpeechListening ? stopSpeechRecognition : startSpeechRecognition}
                disabled={isInputDisabled}
                className={cn(
                  'ml-1 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl border transition-colors duration-150',
                  isSpeechListening
                    ? 'border-red-400/50 bg-red-500/20 text-red-200 animate-pulse'
                    : 'border-border bg-card text-foreground hover:border-blue-500/40 hover:text-foreground-bright',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
                title={isSpeechListening ? 'Stop listening' : 'Start voice input'}
                aria-label={isSpeechListening ? 'Stop voice input' : 'Start voice input'}
                aria-pressed={isSpeechListening}
              >
                <Mic className={cn('h-4 w-4', isSpeechListening && 'text-red-100')} />
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={!hasContent || isInputDisabled || isOverLimit || hasUploadErrors}
              className={cn(
                'ml-1 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl transition-colors duration-150',
                hasContent && !isInputDisabled && !isOverLimit && !hasUploadErrors
                  ? 'bg-blue-600 text-white hover:bg-blue-500'
                  : 'cursor-not-allowed bg-muted/30 text-muted-foreground',
              )}
              title={hasUploadErrors ? 'Fix upload errors first' : `Send message (Enter or ${sendShortcut})`}
              aria-label="Send message"
            >
              {isSubmitting ? (
                <div className="ally-spinner-ring h-4 w-4 border-white/30 border-t-white" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={SUPPORTED_FILE_TYPES.join(',')}
            onChange={handleFileInputChange}
            className="hidden"
            aria-label="Choose files to attach"
          />
          <input
            ref={photoInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
            aria-label="Choose photos to attach"
          />
        </div>
      </div>


      <OneDrivePicker
        isOpen={isOneDriveOpen}
        onClose={() => setIsOneDriveOpen(false)}
        onFilesSelected={(files) => addFiles(files)}
      />
    </div>
  );
}

interface FilePreviewProps {
  fileUpload: FileUpload;
  onRemove: () => void;
}

function FilePreview({ fileUpload, onRemove }: FilePreviewProps) {
  const { file, progress, status, error } = fileUpload;
  const isImage = file.type.startsWith('image/');

  return (
    <div className={cn('flex min-h-[52px] items-center gap-2 rounded-xl border p-2 pr-1 shadow-sm shadow-black/5', status === 'error' ? 'border-red-500/50 bg-red-500/5' : 'border-border bg-background')}>
      {isImage ? (
        <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-card">
          <img
            src={URL.createObjectURL(file)}
            alt={file.name}
            className="h-full w-full object-cover"
            onLoad={(event) => {
              const image = event.target as HTMLImageElement;
              setTimeout(() => URL.revokeObjectURL(image.src), 1000);
            }}
          />
        </div>
      ) : (
        <div className={cn('flex h-8 w-8 items-center justify-center rounded', status === 'error' ? 'bg-red-500/10' : 'bg-blue-500/10')}>
          <Paperclip className={cn('h-4 w-4', status === 'error' ? 'text-red-300' : 'text-blue-300')} />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-foreground" title={file.name}>
          {file.name}
        </p>
        <div className="text-xs text-muted-foreground">
          <span>{formatFileSize(file.size)}</span>
          {status === 'uploading' ? <span className="ml-2">{progress}%</span> : null}
          {status === 'error' && error ? <span className="ml-2 text-red-300">{error}</span> : null}
        </div>
        {status === 'uploading' ? (
          <div className="mt-1 h-1 w-full rounded-full bg-muted/40">
            <div className="h-1 rounded-full bg-blue-500 transition-all duration-200" style={{ width: `${progress}%` }} />
          </div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="rounded-lg p-1 text-muted-foreground transition-colors duration-150 hover:bg-red-500/10 hover:text-red-300"
        title={`Remove ${file.name}`}
        aria-label={`Remove ${file.name}`}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

export default MessageInput;
